import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Bed } from 'lucide-react';
import Layout from '@/components/Layout';

export default function OccupancyPage() {
  const [pgs, setPgs] = useState([]);
  const [selectedPg, setSelectedPg] = useState('');
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPgs();
  }, []);

  useEffect(() => {
    if (selectedPg) {
      fetchOccupancyData();
    }
  }, [selectedPg]);

  const fetchPgs = async () => {
    try {
      const response = await api.getPGs();
      setPgs(response.data);
      if (response.data.length > 0) {
        setSelectedPg(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to fetch PGs');
      setLoading(false);
    }
  };

  const fetchOccupancyData = async () => {
    try {
      const floorsRes = await api.getFloors(selectedPg);
      const floorsData = floorsRes.data;
      setFloors(floorsData);

      const roomsPromises = floorsData.map(floor => api.getRooms(floor.id));
      const roomsResults = await Promise.all(roomsPromises);
      const roomsData = roomsResults.flatMap(res => res.data);
      setRooms(roomsData);

      const bedsPromises = roomsData.map(room => api.getBeds(room.id));
      const bedsResults = await Promise.all(bedsPromises);
      const bedsData = bedsResults.flatMap(res => res.data);
      setBeds(bedsData);
    } catch (error) {
      toast.error('Failed to fetch occupancy data');
    } finally {
      setLoading(false);
    }
  };

  const getBedsForRoom = (roomId) => beds.filter(bed => bed.roomId === roomId);
  const getRoomsForFloor = (floorId) => rooms.filter(room => room.floorId === floorId);

  const totalBeds = beds.length;
  const occupiedBeds = beds.filter(b => b.status === 'occupied').length;
  const vacantBeds = totalBeds - occupiedBeds;
  const occupancyRate = totalBeds > 0 ? ((occupiedBeds / totalBeds) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading">Occupancy View</h1>
            <p className="text-slate-600 mt-1">Visual layout of rooms and beds</p>
          </div>
          {pgs.length > 1 && (
            <select
              value={selectedPg}
              onChange={(e) => setSelectedPg(e.target.value)}
              className="h-11 px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="occupancy-pg-selector"
            >
              {pgs.map((pg) => (
                <option key={pg.id} value={pg.id}>{pg.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-700 mb-1">Total Beds</p>
            <p className="text-3xl font-bold text-slate-900">{totalBeds}</p>
          </div>
          <div className="bg-green-50 rounded-xl border border-green-200 p-5 shadow-sm">
            <p className="text-sm font-semibold text-green-700 mb-1">Vacant Beds</p>
            <p className="text-3xl font-bold text-green-600">{vacantBeds}</p>
          </div>
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-5 shadow-sm">
            <p className="text-sm font-semibold text-blue-700 mb-1">Occupancy Rate</p>
            <p className="text-3xl font-bold text-blue-600">{occupancyRate}%</p>
          </div>
        </div>

        {floors.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Bed className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">No rooms setup yet</h2>
            <p className="text-slate-600">Please setup your PG structure first</p>
          </div>
        ) : (
          <div className="space-y-6">
            {floors.map((floor) => {
              const floorRooms = getRoomsForFloor(floor.id);
              return (
                <div key={floor.id} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 font-heading">{floor.floorName}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {floorRooms.map((room) => {
                      const roomBeds = getBedsForRoom(room.id);
                      const occupied = roomBeds.filter(b => b.status === 'occupied').length;
                      const vacant = roomBeds.length - occupied;
                      return (
                        <div
                          key={room.id}
                          className="border-2 border-slate-200 rounded-lg p-4"
                          data-testid={`room-${room.id}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-slate-900">Room {room.roomNumber}</h3>
                            <span className="text-xs font-semibold px-2 py-1 bg-slate-100 text-slate-700 rounded">
                              {room.roomType}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            {roomBeds.map((bed) => (
                              <div
                                key={bed.id}
                                className={`p-3 rounded-lg border-2 text-center ${
                                  bed.status === 'occupied'
                                    ? 'bg-blue-100 border-blue-300'
                                    : 'bg-green-100 border-green-300'
                                }`}
                                data-testid={`bed-${bed.id}`}
                              >
                                <Bed className={`w-6 h-6 mx-auto mb-1 ${
                                  bed.status === 'occupied' ? 'text-blue-600' : 'text-green-600'
                                }`} />
                                <p className={`text-xs font-bold ${
                                  bed.status === 'occupied' ? 'text-blue-900' : 'text-green-900'
                                }`}>
                                  Bed {bed.bedNumber}
                                </p>
                                <p className={`text-xs ${
                                  bed.status === 'occupied' ? 'text-blue-700' : 'text-green-700'
                                }`}>
                                  {bed.status === 'occupied' ? 'Occupied' : 'Vacant'}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-xs text-slate-600 pt-2 border-t border-slate-200">
                            <span>Occupied: {occupied}</span>
                            <span>Vacant: {vacant}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
